import { z } from 'zod';

export const CoPilotModelSelectionSchema = z.enum([
  'balanced',
  'fast',
  'powerful',
]);

export type CoPilotModelSelection = z.infer<typeof CoPilotModelSelectionSchema>;

export const COPILOT_MODEL_SELECTION_OPTIONS =
  CoPilotModelSelectionSchema.options;

export const DEFAULT_COPILOT_MODEL_SELECTION: CoPilotModelSelection =
  'balanced';

export const CoPilotRunModelSchema = z.union([
  CoPilotModelSelectionSchema,
  z.string().min(1),
]);

export type CoPilotRunModel = z.infer<typeof CoPilotRunModelSchema>;

export const parseCoPilotModelSelection = ({
  value,
}: {
  value: unknown;
}): CoPilotModelSelection => {
  const parsed = CoPilotModelSelectionSchema.safeParse(value);

  if (parsed.success) {
    return parsed.data;
  }

  return DEFAULT_COPILOT_MODEL_SELECTION;
};

export const isCoPilotModelSelection = (
  value: string,
): value is CoPilotModelSelection =>
  CoPilotModelSelectionSchema.safeParse(value).success;

export const parseCoPilotRunModel = ({
  value,
  eligibleTerraModelSlugs,
}: {
  value: unknown;
  eligibleTerraModelSlugs?: readonly string[];
}): CoPilotRunModel => {
  if (typeof value !== 'string' || value.length === 0) {
    return DEFAULT_COPILOT_MODEL_SELECTION;
  }

  if (isCoPilotModelSelection(value)) {
    return value;
  }

  if (eligibleTerraModelSlugs && eligibleTerraModelSlugs.includes(value)) {
    return value;
  }

  if (!eligibleTerraModelSlugs) {
    return value;
  }

  return DEFAULT_COPILOT_MODEL_SELECTION;
};

export const CoPilotAgentModelSlotSchema = z.enum([
  'mainAgent',
  'recon',
  'operator',
  'applicationDataAnalyst',
  'objectCreator',
  'skillBuilder',
]);

export type CoPilotAgentModelSlot = z.infer<typeof CoPilotAgentModelSlotSchema>;

export const CoPilotAgentModelOverridesSchema = z
  .object({
    mainAgentModel: z.string().min(1).optional(),
    reconModel: z.string().min(1).optional(),
    operatorModel: z.string().min(1).optional(),
    applicationDataAnalystModel: z.string().min(1).optional(),
    objectCreatorModel: z.string().min(1).optional(),
    skillBuilderModel: z.string().min(1).optional(),
  })
  .partial();

export type CoPilotAgentModelOverrides = z.infer<
  typeof CoPilotAgentModelOverridesSchema
>;

export const CoPilotAgentModelsConfigSchema = z.object({
  mainAgentModel: z.string().min(1),
  reconModel: z.string().min(1),
  operatorModel: z.string().min(1),
  applicationDataAnalystModel: z.string().min(1),
  objectCreatorModel: z.string().min(1),
  skillBuilderModel: z.string().min(1),
});

export type CoPilotAgentModelsConfig = z.infer<
  typeof CoPilotAgentModelsConfigSchema
>;

export const COPILOT_AGENT_MODEL_SLOTS: Array<{
  slot: CoPilotAgentModelSlot;
  configKey: keyof CoPilotAgentModelsConfig;
  label: string;
}> = [
  {
    slot: 'mainAgent',
    configKey: 'mainAgentModel',
    label: 'Main agent',
  },
  {
    slot: 'recon',
    configKey: 'reconModel',
    label: 'Recon',
  },
  {
    slot: 'operator',
    configKey: 'operatorModel',
    label: 'Conductor',
  },
  {
    slot: 'applicationDataAnalyst',
    configKey: 'applicationDataAnalystModel',
    label: 'Data Analyst',
  },
  {
    slot: 'objectCreator',
    configKey: 'objectCreatorModel',
    label: 'Scribe',
  },
  {
    slot: 'skillBuilder',
    configKey: 'skillBuilderModel',
    label: 'Skill Builder',
  },
];
